import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BackgroundCheckReport from './BackgroundCheckReport/BackgroundCheckReport';
import CreditCheckReport from './CreditCheckReport/CreditCheckReport';
import { useEffect, useState } from 'react';
import { ApplicantScreeningReportDto, ScreeningStatus } from '../types';
import useApplicantScreeningReport from 'hooks/query/offers/use-get-applicant-screening-report';
import { Loader } from 'legacy-components/componets';
import BlurredContainer from 'components/organisms/BlurredContainer/BlurredContainer';
import { ScreeningCheckStatus } from 'common/types/services/api/offers/offer-item-dto.type';
import { OfferStatusDto } from 'common/enums/enums';
import { isRequestBackgroundCheckAvailable } from '../../offers/common/helpers/offer-status-actions';

interface Props {
  renterId: string;
  applicationId: string;
  offerStatus?: OfferStatusDto;
  location?: string;
}

type ScreeningType = 'background' | 'credit';

const TenantScreeningSection = ({ renterId, applicationId, offerStatus, location }: Props) => {
  const screeningCheckBlurredTitles = 
  {
    screeningIsntAvailable: (screening: ScreeningType) => `The ${screening} check has not been initiated`,
    screningNotRequested: (screening: ScreeningType) => `The ${screening} check has not yet been initiated. Please click on the “Request Background Check” button at the top of this page to request a background check for the tenant(s).`,
    screningRequested: (screening: ScreeningType) => `The ${screening} check has been requested and is currently waiting for tenants’ approval. Axxelist will notify you when background check request is approved by tenants.`,
    screningRequestedAndPreAcceptedRenter: (screening: ScreeningType) => `The tenants have pre-consented to the ${screening} check. The landlord has initiated the process and selected the tenant-paid option. Axxelist is currently awaiting payment confirmation from the tenants. Axxelist will notify you once the payment is completed.`,
    screningRequestedRenter: (screening: ScreeningType) => `The ${screening} check has been requested. The landlord has initiated the process and selected the tenant-paid option. Axxelist is currently awaiting for approval and payment confirmation from the tenants. Axxelist will notify you once the payment is completed.`,
    screningPending: (screening: ScreeningType) => `Thank you for your patience. The ${screening} check request has been approved by tenants. The background check has been initiated and is currently in progress. Typically, this process is completed within 24 hours. We will notify you once it has been completed. Thank you for your patience!`,
  };

  const getBlurredScreeningCheckTitle = (screening: ScreeningType, applicant: ApplicantScreeningReportDto) =>  {
    const isAllRunScreeningAccepted = applicant.isAllRunScreeningAccepted;
    const screeningCheckStatus = screening === 'background' ? applicant.backgroundScreeningCheckStatus : applicant.creditScreeningCheckStatus;
    const status = screening === 'background' ? applicant.backgroundCheckStatus : applicant.creditCheckStatus;

    if (offerStatus && !isRequestBackgroundCheckAvailable(offerStatus)) {
      return screeningCheckBlurredTitles.screeningIsntAvailable(screening);
    }

    if (!status) {
      return screeningCheckBlurredTitles.screningNotRequested(screening);
    }
  
    if (status === ScreeningStatus.Pending) {
      return screeningCheckBlurredTitles.screningPending(screening);
    }
  
    if (status === ScreeningStatus.Prepared) {
      const isRequestedLandlord = screeningCheckStatus === ScreeningCheckStatus.RequestedLandlord;
      const isRequestedRenter = screeningCheckStatus === ScreeningCheckStatus.RequestedRenter;
  
      if (isAllRunScreeningAccepted) {
        if (isRequestedLandlord) {
          return screeningCheckBlurredTitles.screningPending(screening);
        }
  
        if (isRequestedRenter) {
          return screeningCheckBlurredTitles.screningRequestedAndPreAcceptedRenter(screening);
        }
      } else {
        if (isRequestedLandlord) {
          return screeningCheckBlurredTitles.screningRequested(screening);
        }
  
        if (isRequestedRenter) {
          return screeningCheckBlurredTitles.screningRequestedRenter(screening);
        }
  
        return screeningCheckBlurredTitles.screningRequested(screening);
      }
    }
  
    return screeningCheckBlurredTitles.screningNotRequested(screening);
  };
  const reportStyles = 'flex-col basis-1/2 justify-between border-[1.5px] border-gray rounded-lg py-3';

  const [screeningReports, setScreeningReports] = useState<ApplicantScreeningReportDto[]>(
    [
      {
        firstName: '',
        lastName: '',
        isTenant: true,
        isAllRunScreeningAccepted: false,
        creditScreeningCheckStatus: ScreeningCheckStatus.NotRequested,
        backgroundScreeningCheckStatus: ScreeningCheckStatus.NotRequested
      }
    ]
  );
  const { data: applicantScreeningReport, isLoading: isScreeningDataLoading } = useApplicantScreeningReport(renterId, applicationId, location);

  useEffect(() => {
    if (!applicantScreeningReport) return;
    setScreeningReports(applicantScreeningReport);
  }, [applicantScreeningReport]);

  if (isScreeningDataLoading) {
    return <Loader />;
  }

  return (
    <div className='p-6 border-[1.5px] border-gray rounded-lg'>
      <h3 className='text-2xl font-bold leading-normal pb-5'>Background/Credit check information</h3>
      {!!screeningReports?.length && screeningReports.map((applicant, index) => (
        <Accordion key={index} defaultExpanded={applicant.isTenant}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <h3 className='text-xl font-bold'>{applicant.firstName + ' ' + applicant.lastName}</h3>
          </AccordionSummary>
          <AccordionDetails className='flex gap-4'>
            <BlurredContainer styles={reportStyles} isBlurred={!applicant.backgroundCheckResult} blurredTitle={getBlurredScreeningCheckTitle('background', applicant)}>
              <BackgroundCheckReport report={applicant.backgroundCheckResult} isBlurred={!applicant.backgroundCheckResult} />
            </BlurredContainer>
            <BlurredContainer styles={reportStyles} isBlurred={!applicant.creditCheckResult} blurredTitle={getBlurredScreeningCheckTitle('credit', applicant)}>
              <CreditCheckReport report={applicant.creditCheckResult} isBlurred={!applicant.creditCheckResult} />
            </BlurredContainer>
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
}

export default TenantScreeningSection